/**
 * The quiet UI's class strings (DESIGN.md "Color", 2026-10-29; research in
 * docs/research/calm-brand-color-mobbin-2026-10-29.md). Solid indigo is kept
 * for the one primary action on a screen, focus rings, the logo and toggles
 * that are on; a picked or current thing wears the faint brand tint (the
 * SELECTED family); everything else here is grey. One place, so a link, a
 * picked pill, a tooltip or a floating card reads the same on every page.
 *
 * Dark mode needs nothing extra: the grey tokens invert and the indigo
 * 50/200/700 steps turn into a dim tint with a light text (globals.css), so
 * bg-white, text-gray-900 and the tints flip on their own. Never add
 * dark:*-gray-* variants next to these.
 */
import type { CSSProperties } from "react";

/** The line under every text link: a quiet grey underline at rest that
 *  darkens on hover (owner, 2026-10-31: links read as plain dark text,
 *  "Ətraflı" did not look like a link). */
const LINK_LINE = "underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900";

/** A standalone text link ("Hamısı", "Xəritədə açın", "Ətraflı"): dark grey,
 *  semibold and underlined, so it reads as a link without a colour. Add a
 *  grey chevron (CHEVRON) where the link leads on. */
export const LINK = `font-semibold text-gray-900 ${LINK_LINE}`;

/** A link inside running text: the same line at a lighter weight. */
export const INLINE_LINK = `font-medium text-gray-900 ${LINK_LINE}`;

/** The grey chevron or arrow beside a link or in a list row. */
export const CHEVRON = "text-gray-400";

/** A picked pill, chip, option card or room toggle (controls with their own
 *  border), and a filter with a value: the bar's pills, the picked options
 *  and filled boxes in the filter panel, and the applied chips. One token,
 *  so "picked" and "set" read one way: the faint brand tint (owner,
 *  2026-10-31: option D for filters, then the second round: a picked
 *  option should stand out, and the white with a 2 px dark outline turned
 *  into a shiny white ring in dark mode). indigo-50/200 turn into a dim
 *  tint in dark mode and indigo-700 into a light text (globals.css), so no
 *  dark variants. */
export const SELECTED = "border-indigo-200 bg-indigo-50 text-indigo-700";

/** The resting partner of SELECTED. */
export const UNSELECTED = "border-gray-200 bg-white text-gray-700 hover:border-gray-400";

/** The picked segment of a segmented control (a track with borderless
 *  segments) and the sliding thumb of the page tabs: the SELECTED tint with
 *  its edge drawn as an inset ring, so nothing shifts (owner, 2026-10-31,
 *  second round; it replaces the raised white segment). */
export const SEGMENT_ON = "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200";

/** The current card among cards (the roadmap's next step, the listing open
 *  in the drawer, the row just added): the white body stays, since pictures
 *  sit on it, and the SELECTED tint draws a 2 px edge (the border plus a
 *  1 px inset ring, so nothing shifts) instead of a dark outline. */
export const CURRENT_CARD = "border-indigo-200 ring-1 ring-inset ring-indigo-200";

/** A form field's box (an input, or a wrapper that holds one with its
 *  unit): the hairline border of every form on the site and the indigo
 *  focus ring (focus rings keep the solid brand colour). `field-surface`
 *  gives a wrapper the dark field fill that real inputs get from
 *  globals.css. Pair it with `rounded-lg border`. */
export const FIELD =
  "field-surface border-gray-200 bg-white focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100";

/** How many filters are set, on the button that opens them and on the
 *  panel's sections (owner, 2026-10-31): the solid form of the same brand
 *  colour, so the count and the tinted pills read as one family. */
export const FILTER_COUNT = "bg-indigo-600 text-white";

/** A ticked box in the filter UI. A faint tint would hide the tick, so the
 *  box is solid brand, as on Zillow and Booking; black was the last black
 *  "set" mark among the tinted ones. */
export const FILTER_CHECK = "border-indigo-600 bg-indigo-600 text-white";

/** An unpicked segment. */
export const SEGMENT_OFF = "text-gray-500 hover:text-gray-900";

/** The current row in a menu or a popover list (Linear): a grey fill and
 *  semibold dark text; pair it with a check mark where the list picks. */
export const ROW_ON = "bg-gray-100 font-semibold text-gray-900";

/** Tooltips, coach marks and hints: near-black with light text. With the
 *  inverted grey tokens it turns into a light bubble on the dark theme. */
export const TOOLTIP = "bg-gray-900 text-gray-50";

/** A floating card (a prompt, an offer, a toast): white, the hairline border
 *  and a soft shadow. Only its main button is indigo; tags inside are TAG.
 *  `bg-paper` (white, the dark card surface in dark mode), not `bg-white`:
 *  globals.css turns a fixed `bg-white` into a see-through bar in dark mode. */
export const FLOAT_CARD =
  "border border-gray-200 bg-paper text-gray-900 shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)]";

/** A neutral tag, pill or numbered badge. */
export const TAG = "bg-gray-100 text-gray-700";

/** The "Pro" badge: a dark pill (light on the dark theme). */
export const PRO_BADGE = "bg-gray-900 text-gray-50";

/** A count on a control (set filters): dark, like the Pro badge. */
export const COUNT_BADGE = "bg-gray-900 text-gray-50";

/** Unread or "needs you" dot: rose, as the big apps do. */
export const UNREAD_DOT = "bg-rose-500";

/** "Something new inside" dot on a menu item: a quiet grey, shown for at
 *  most NEW_MARK_DAYS after the launch (lib/announcements). */
export const NEW_DOT = "bg-gray-400";

/** An avatar without a photo: grey fill, grey-700 initials. */
export const AVATAR = "bg-gray-200 text-gray-700";

/** A second action, or a widget's action on a page whose one primary is
 *  elsewhere (the hub's search): white, the hairline border, dark grey
 *  text. Only the screen's one primary action is solid indigo. */
export const BUTTON_SECONDARY = "border border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50";

/** A small icon in a list, a checklist or a section header. */
export const ICON = "text-gray-400";

/** The filled part of a range slider: every `input[type="range"]` shares
 *  one quiet style (globals.css: a grey track, a dark thumb, the part up to
 *  the thumb filled in dark grey), and this tells the track how far to
 *  fill. Pass it as the input's `style`. */
export function rangeFill(value: number, min: number, max: number): CSSProperties {
  const span = max - min;
  const pct = span > 0 ? Math.min(100, Math.max(0, ((value - min) / span) * 100)) : 0;
  return { "--range-pct": `${pct}%` } as CSSProperties;
}
